import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents, createViewEventElement } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { debounce, onAnimationEnd, prefersReducedMotion, startViewTransition } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { bindOverflowCarousel } from '@theme/bt-scroll-carousel';
import { CART_DRAWER_SECTION_ID, CartApiError, OWN_EVENT, PREVIEW_LINE, add, change } from '@theme/bt-cart-api';
import { getNostoApi } from '@theme/bt-nosto-search';
import {
  CrossSellSlot,
  campaignFromResponse,
  campaignProducts,
  productHandle,
  trackingAllowed,
} from '@theme/bt-cart-cross-sell';
import { productKey, toProduct, toggle as toggleWishlistEntry, wishlistKeys } from '@theme/bt-wishlist';

const QUANTITY_DEBOUNCE = 300;

const DRAG_THRESHOLD = 4;
const SCROLLED_ENTER = 16;
const SCROLLED_LEAVE = 1;
const SCROLLED_MIN_OVERFLOW = 48;
const TRANSITION_EMPTY = 'bt-cart-drawer-empty';
const ADD_MARKER = 'bt-cart-drawer:add';

/** Standard events carry merchandise ids as GIDs; the drawer rows carry plain variant ids. */
function variantIdOf(value) {
  return String(value ?? '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
}
const CARD_ID_SUFFIX = '-btcd';
const GWP_BLOCK_KEY = 'bt-cart-gwp-blocked';
const GWP_MAX_CHAIN = 3;

export class BtCartDrawer extends createViewEventElement(DialogComponent) {
  requiredRefs = ['dialog', 'body'];

  #abortController = null;
  #lineRequests = new Map();
  #lineCommits = new Map();
  #lineBaseline = new Map();
  #linePending = new Map();
  #carousels = [];
  #crossSell = new CrossSellSlot(this);
  #recentlyViewedController = null;
  #showPending = false;
  #closeRequested = false;

  #drag = null;

  #dragSuppressClick = false;
  #itemCount = 0;
  #freeDeliveryReached = null;

  #bundleSets = null;
  #pendingNostoAdd = null;
  #wishlist = null;
  #giftRequest = null;
  #pendingAdds = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onQuantityInputChange, { signal });

    this.addEventListener('click', this.#onClickCapture, { signal, capture: true });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerEnd, { signal });
    this.addEventListener('pointercancel', this.#onPointerEnd, { signal });
    this.addEventListener('scroll', this.#onScroll, { signal, capture: true, passive: true });

    this.#syncDerivedState();
    this.#syncTriggers();

    this.#suffixCardIds();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    for (const commit of this.#lineCommits.values()) commit.cancel();
    for (const controller of this.#lineRequests.values()) controller.abort();
    this.#lineCommits.clear();
    this.#lineRequests.clear();
    for (const carousel of this.#carousels) carousel.disconnect();
    this.#carousels = [];
    this.#crossSell.destroy();
    this.#recentlyViewedController?.abort();
    this.#giftRequest?.abort();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;

    this.toggleAttribute('open', true);
    this.#syncTriggers();

    this.#showPending = true;
    this.showDialog();

    requestAnimationFrame(() => {
      this.#showPending = false;
      if (this.#closeRequested) {
        this.#closeRequested = false;
        this.closeDialog();
      }
    });
  }

  async close() {
    if (!this.isOpen) return;

    if (this.#showPending) {
      this.#closeRequested = true;
      return;
    }

    await this.closeDialog();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #onDialogOpen = () => {
    this.toggleAttribute('open', true);
    this.#syncTriggers();
    this.refs.closeButton?.focus({ preventScroll: true });

    this.#syncDerivedState();
    this.#bindCarousels();
    this.#crossSell.sync().then(() => this.#bindCarousels());
    if (this.dataset.state === 'empty') this.#fillRecentlyViewed();

    this.#syncWishlist();
    this.#syncScrolled();
    this.#syncGift();

    this.dispatchViewEvent();
    this.dispatchEvent(new BtCartDrawerOpenEvent(this.#itemCount));
  };

  #onScroll = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && target.matches('.bt-cart-drawer__scroll')) {
      this.#syncScrolled(target);
    }
  };

  #syncScrolled(scroller = this.refs.body.querySelector('.bt-cart-drawer__scroll')) {
    if (!(scroller instanceof HTMLElement)) {
      this.removeAttribute('data-scrolled');
      return;
    }

    if (this.hasAttribute('data-scrolled')) {
      if (scroller.scrollTop <= SCROLLED_LEAVE) this.removeAttribute('data-scrolled');
      return;
    }

    const overflow = scroller.scrollHeight - scroller.clientHeight;
    const bars = Array.from(this.refs.body.querySelectorAll('.bt-cart-drawer__progress')).filter(
      (bar) => bar.getClientRects().length > 0
    ).length;
    if (scroller.scrollTop > SCROLLED_ENTER && overflow > SCROLLED_MIN_OVERFLOW * Math.max(1, bars)) {
      this.toggleAttribute('data-scrolled', true);
    }
  }

  #onDialogClose = () => {
    this.removeAttribute('open');
    this.#syncTriggers();

    for (const carousel of this.#carousels) carousel.disconnect();
    this.#carousels = [];

    this.#clearLineErrors();

    this.dispatchEvent(new BtCartDrawerCloseEvent());

    const active = document.activeElement;
    if (!active || active === document.body || this.contains(active)) {
      const trigger = document.querySelector(`[aria-controls="${this.id}"]`);
      if (trigger instanceof HTMLElement) trigger.focus();
    }
  };

  #syncTriggers() {
    const expanded = this.isOpen ? 'true' : 'false';
    for (const trigger of document.querySelectorAll(`[aria-controls="${this.id}"]`)) {
      trigger.setAttribute('aria-expanded', expanded);
    }
  }

  #setState(state) {
    this.dataset.state = state;

    const busy = state === 'loading' || state === 'updating';
    if (busy) {
      this.refs.dialog.setAttribute('aria-busy', 'true');
    } else {
      this.refs.dialog.removeAttribute('aria-busy');
    }
  }

  #setLineState(lineKey, state, message = '') {
    const row = this.#lineRow(lineKey);
    if (!row) return;

    row.dataset.lineState = state;

    const error = row.querySelector('[data-line-error]');
    if (error instanceof HTMLElement) {
      error.textContent = message;
      error.hidden = state !== 'error' || !message;
    }
  }

  #syncDerivedState() {
    const { body } = this.refs;

    const empty = body.dataset.cartEmpty === 'true';
    const reached = body.dataset.freeDeliveryReached === 'true';
    const bundleSets = Number(body.dataset.bundleSets ?? 0);

    this.#itemCount = Number(body.dataset.cartItemCount ?? 0);
    this.#setState(
      this.#pendingAdds.size ? 'loading' : this.#lineRequests.size ? 'updating' : empty ? 'empty' : 'idle'
    );

    if (reached && this.#freeDeliveryReached === false) {
      this.#announce(this.dataset.labelA11yProgressReached ?? '');
    }
    if (this.#bundleSets !== null && bundleSets > this.#bundleSets) {
      this.#announce(this.dataset.labelA11yBundleUnlocked ?? '');
    }

    this.#freeDeliveryReached = reached;
    this.#bundleSets = bundleSets;

    this.#syncGiftBlocked();
  }

  #clearLineErrors() {
    for (const row of this.refs.body.querySelectorAll('[data-line-state="error"]')) {
      if (!(row instanceof HTMLElement)) continue;
      row.dataset.lineState = 'idle';
      const error = row.querySelector('[data-line-error]');
      if (error instanceof HTMLElement) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    const region = this.refs.liveRegion;
    if (region instanceof HTMLElement) region.textContent = '';
  }

  #announce(message) {
    const region = this.refs.liveRegion;
    if (!(region instanceof HTMLElement) || !message) return;

    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  async #applySection(html, { types, resolving = null } = {}) {
    if (!html) return;

    const apply = () => morphSection(CART_DRAWER_SECTION_ID, html, { mode: 'hydration' });

    // Placeholders of other in-flight adds are not in this response: keep them out of the
    // morph (an unkeyed row would be morphed into the first real line) and put them back after.
    const detached = this.#detachPendingPlaceholders(resolving);

    try {
      if (types?.length) {
        await startViewTransition(apply, types);
      } else {
        await apply();
      }

      this.#afterMorph();
    } finally {
      this.#restorePendingPlaceholders(detached);
    }
  }

  #afterMorph() {
    this.updatedCallback();
    this.#syncDerivedState();
    this.#reconcilePendingLines();
    this.#syncNostoCart();
    this.#suffixCardIds();
    this.#paintWishlist();
    this.#bindCarousels();
    this.#crossSell.sync().then(() => this.#bindCarousels());

    if (this.dataset.state === 'empty') this.#fillRecentlyViewed();
    this.#resetScrollOnEmpty();
    this.#syncScrolled();
    this.#recoverFocus();
    this.#syncGift();

    this.dispatchEvent(
      new BtCartDrawerUpdatedEvent({
        itemCount: this.#itemCount,
        empty: this.dataset.state === 'empty',
      })
    );
  }

  async #syncGift(depth = 0) {
    if (
      this.hasAttribute('data-design-mode') ||
      this.#giftRequest ||
      this.#lineRequests.size ||
      this.#pendingAdds.size
    ) {
      return;
    }

    const { body } = this.refs;
    const state = body.dataset.gwpState ?? 'off';
    const lineKey = body.dataset.gwpLineKey ?? '';
    const variantId = body.dataset.gwpVariantId ?? '';
    const keepUnpriced = this.#keepUnpriced();

    if (state === 'off' || state === 'added') return;
    if (state === 'unpriced' && keepUnpriced) return;
    if (state !== 'unlocked' && !lineKey) return;
    if (state === 'unlocked' && (!variantId || (!keepUnpriced && this.#giftBlocked()))) return;

    const controller = new AbortController();
    this.#giftRequest = controller;
    this.#setState('updating');
    let settled = false;

    try {
      if (state === 'unlocked') {
        const { cart, sections } = await add(
          [{ id: Number(variantId), quantity: 1, properties: { _bt_gwp: '1' } }],
          { source: this, signal: controller.signal }
        );

        const added = (cart?.items ?? []).find((item) => String(item?.variant_id) === variantId) ?? null;
        const free = added ? Number(added.final_line_price ?? 0) === 0 : false;
        const preorder = Boolean(added?.properties?.__releaseId);

        if (added && (preorder || (!free && !keepUnpriced))) {
          this.#blockGift();
          const removed = await change(added.key, 0, { source: this, signal: controller.signal });
          await this.#applySection(removed.sections[CART_DRAWER_SECTION_ID]);
        } else {
          await this.#applySection(sections[CART_DRAWER_SECTION_ID]);
          this.#announce(this.dataset.labelA11yGiftAdded ?? '');
        }
      } else if (state === 'excess') {
        const { sections } = await change(lineKey, 1, { source: this, signal: controller.signal });
        await this.#applySection(sections[CART_DRAWER_SECTION_ID]);
      } else {
        if (state === 'unpriced') this.#blockGift();

        const { sections } = await change(lineKey, 0, { source: this, signal: controller.signal });
        await this.#applySection(sections[CART_DRAWER_SECTION_ID]);
        if (state === 'progress' || state === 'stale') this.#announce(this.dataset.labelA11yGiftRemoved ?? '');
      }

      settled = true;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#blockGift();
      console.warn('[bt-cart-drawer] Free gift sync failed', error);
    } finally {
      if (this.#giftRequest === controller) this.#giftRequest = null;
      this.#syncDerivedState();
      if (settled && depth < GWP_MAX_CHAIN) this.#syncGift(depth + 1);
    }
  }

  #keepUnpriced() {
    return this.refs.body.dataset.gwpUnpriced === 'keep';
  }

  #giftBlocked() {
    try {
      return window.sessionStorage.getItem(GWP_BLOCK_KEY) === '1';
    } catch {
      return false;
    }
  }

  #blockGift() {
    try {
      window.sessionStorage.setItem(GWP_BLOCK_KEY, '1');
    } catch {
      return;
    } finally {
      this.#syncGiftBlocked();
    }
  }

  #syncGiftBlocked() {
    this.toggleAttribute('data-gwp-blocked', !this.#keepUnpriced() && this.#giftBlocked());
  }

  #resetScrollOnEmpty() {
    if (this.dataset.state !== 'empty') return;
    const region = this.refs.body.querySelector('.bt-cart-drawer__empty');
    if (region instanceof HTMLElement) region.scrollTop = 0;
  }

  #recoverFocus() {
    if (!this.isOpen) return;

    for (const dialog of document.querySelectorAll('dialog:modal')) {
      if (dialog !== this.refs.dialog) return;
    }

    const active = document.activeElement;
    if (active && active !== document.body && this.contains(active)) return;

    this.refs.closeButton?.focus({ preventScroll: true });
  }

  #reconcilePendingLines() {
    for (const lineKey of this.#lineRequests.keys()) this.#setLineState(lineKey, 'busy');

    for (const [lineKey, quantity] of this.#linePending) {
      const input = this.#lineInput(lineKey);
      if (input) input.value = String(quantity);
      this.#updateStepperBounds(lineKey, quantity);
    }
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const step = target.closest('[data-qty-minus], [data-qty-plus]');
    if (step instanceof HTMLElement) {
      event.preventDefault();
      this.#stepQuantity(step, step.hasAttribute('data-qty-plus') ? 1 : -1);
      return;
    }

    const remove = target.closest('[data-line-remove]');
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      this.#removeLine(this.#lineKeyOf(remove));
      return;
    }

    const crossSellAdd = target.closest('[data-cross-sell-add]');
    if (crossSellAdd instanceof HTMLElement) {
      event.preventDefault();
      this.#openQuickAdd(crossSellAdd);
      return;
    }

    const look = target.closest('[data-look-open]');
    if (look instanceof HTMLElement) {
      event.preventDefault();
      this.#openShopTheLook(look.dataset.lookId ?? '', look);
      return;
    }

    const wishlist = target.closest('[data-line-wishlist]');
    if (wishlist instanceof HTMLElement) {
      this.#toggleWishlist(wishlist);
      return;
    }

    if (target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('input, select, textarea')) return;

    const sizes = target.closest('[data-size-overlay]');
    const track = sizes ?? target.closest('[data-bt-cart-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    this.#drag = {
      track,
      scrollable: track.scrollWidth - track.clientWidth > 1,
      pointerId: event.pointerId,
      startX: event.clientX,
      startLeft: track.scrollLeft,
      moved: false,
    };
  };

  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;

    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      drag.moved = true;

      if (drag.scrollable) {
        drag.track.dataset.dragging = 'true';

        try {
          drag.track.setPointerCapture(drag.pointerId);
        } catch {
        }
      }
    }

    if (!drag.scrollable) return;

    event.preventDefault();
    drag.track.scrollLeft = drag.startLeft - dx;
  };

  #onPointerEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.#drag = null;

    if (!drag.moved) return;

    if (drag.scrollable) {
      delete drag.track.dataset.dragging;
      try {
        if (drag.track.hasPointerCapture(drag.pointerId)) {
          drag.track.releasePointerCapture(drag.pointerId);
        }
      } catch {
      }
    }

    this.#dragSuppressClick = true;
  };

  #onClickCapture = (event) => {
    if (!this.#dragSuppressClick) return;
    this.#dragSuppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #stepQuantity(button, direction) {
    const lineKey = this.#lineKeyOf(button);
    const input = this.#lineInput(lineKey);
    if (!lineKey || !input) return;

    const { min, max, step } = this.#bounds(input);

    const typed = Number.parseInt(input.value, 10);
    const rendered = Number.parseInt(input.defaultValue, 10);
    const current =
      this.#linePending.get(lineKey) ??
      (Number.isFinite(typed) ? typed : Number.isFinite(rendered) ? rendered : min);
    const next = Math.min(max, Math.max(min, current + direction * step));

    if (next !== current) this.#queueQuantity(lineKey, next, input);
  }

  #onQuantityInputChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-qty-input]')) return;

    const lineKey = this.#lineKeyOf(input);
    const { min, max } = this.#bounds(input);
    const parsed = Number.parseInt(input.value, 10);

    this.#queueQuantity(
      lineKey,
      Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : min,
      input
    );
  }

  #queueQuantity(lineKey, quantity, input) {
    if (!lineKey) return;

    if (!this.#lineBaseline.has(lineKey)) {
      this.#lineBaseline.set(lineKey, Number(input.defaultValue) || Number(input.value) || 1);
    }

    this.#linePending.set(lineKey, quantity);
    input.value = String(quantity);
    this.#updateStepperBounds(lineKey, quantity);
    this.#setLineState(lineKey, 'busy');
    this.#setState('updating');

    let commit = this.#lineCommits.get(lineKey);
    if (!commit) {
      commit = debounce(() => this.#commitQuantity(lineKey), QUANTITY_DEBOUNCE);
      this.#lineCommits.set(lineKey, commit);
    }
    commit();
  }

  async #commitQuantity(lineKey) {
    const quantity = this.#linePending.get(lineKey);
    if (quantity === undefined) return;

    const marker = cartPerformance.createStartingMarker('bt-cart-drawer:change:user-action');

    this.#lineRequests.get(lineKey)?.abort();
    const controller = new AbortController();
    this.#lineRequests.set(lineKey, controller);

    try {
      const { sections } = await change(lineKey, quantity, {
        source: this,
        signal: controller.signal,
      });

      this.#linePending.delete(lineKey);
      this.#lineBaseline.delete(lineKey);
      this.#lineRequests.delete(lineKey);

      await this.#applySection(sections[CART_DRAWER_SECTION_ID]);
    } catch (error) {
      if ((error)?.name === 'AbortError') return;
      this.#rollbackLine(lineKey, error);
    } finally {
      if (this.#lineRequests.get(lineKey) === controller) this.#lineRequests.delete(lineKey);
      cartPerformance.measureFromMarker(marker);
    }
  }

  async #removeLine(lineKey) {
    const row = this.#lineRow(lineKey);
    if (!lineKey || !row) return;

    const isLast = (this.refs.lines ?? []).length === 1;

    this.#lineCommits.get(lineKey)?.cancel();
    this.#lineRequests.get(lineKey)?.abort();

    const controller = new AbortController();
    this.#lineRequests.set(lineKey, controller);
    this.#setLineState(lineKey, 'busy');
    this.#setState('updating');

    const collapse = this.#collapseLine(row);
    const request = change(lineKey, 0, {
      source: this,
      signal: controller.signal,
    });

    try {
      const [, { sections }] = await Promise.all([collapse, request]);

      this.#linePending.delete(lineKey);
      this.#lineBaseline.delete(lineKey);
      this.#lineRequests.delete(lineKey);

      await this.#applySection(sections[CART_DRAWER_SECTION_ID], {
        types: isLast ? [TRANSITION_EMPTY] : undefined,
      });
    } catch (error) {
      this.#expandLine(row);
      if ((error)?.name !== 'AbortError') this.#rollbackLine(lineKey, error);
    } finally {
      if (this.#lineRequests.get(lineKey) === controller) this.#lineRequests.delete(lineKey);
    }
  }

  #collapseLine(row) {
    row.style.setProperty('--bt-cart-drawer-line-height', `${row.getBoundingClientRect().height}px`);
    row.classList.add('bt-cart-drawer__line--removing');

    return prefersReducedMotion()
      ? Promise.resolve()
      : onAnimationEnd(row, undefined, { subtree: false });
  }

  #expandLine(row) {
    row.classList.remove('bt-cart-drawer__line--removing');
    row.style.removeProperty('--bt-cart-drawer-line-height');
  }

  #rollbackLine(lineKey, error) {
    const baseline = this.#lineBaseline.get(lineKey);
    const input = this.#lineInput(lineKey);

    if (input && baseline !== undefined) {
      input.value = String(baseline);
      this.#updateStepperBounds(lineKey, baseline);
    }

    this.#linePending.delete(lineKey);
    this.#lineBaseline.delete(lineKey);

    const message = this.#errorMessage(error);
    this.#setLineState(lineKey, 'error', message);
    this.#setState('error');
    this.#announce(message);
  }

  #errorMessage(error) {
    if (error instanceof CartApiError) {
      if (error.code === 'SERVICE_UNAVAILABLE') return this.dataset.labelErrorNetwork ?? '';
      if (error.code === 'MAXIMUM_EXCEEDED') {
        return error.message || this.dataset.labelErrorQuantity || '';
      }
      return error.message || this.dataset.labelErrorGeneric || '';
    }
    return this.dataset.labelErrorGeneric ?? '';
  }

  #onCartLinesUpdate = (event) => {
    if (event[OWN_EVENT]) return;

    const isAdd = event.action === 'add';
    const sourceModal =
      event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (this.hasAttribute('data-auto-open') && isAdd && !this.isOpen) {
      // A source dialog (quick-add drawer) is still closing: open as soon as it has gone,
      // not after the response, so the placeholder line is visible while Shopify works.
      if (sourceModal) {
        sourceModal.addEventListener('close', () => this.open(), { once: true });
      } else {
        this.open();
      }
    }

    if (!event.promise) {
      this.#syncDerivedState();
      return;
    }

    const pending = isAdd ? this.#beginAdd(event) : null;
    let ok = false;

    event.promise
      .then(async ({ detail }) => {
        if (detail?.didError) return;

        const html =
          detail?.sections?.[CART_DRAWER_SECTION_ID] ||
          (await sectionRenderer.getSectionHTML(CART_DRAWER_SECTION_ID, false));

        this.#pendingAdds.delete(event);
        await this.#applySection(html, { resolving: pending });
        ok = true;

        if (pending) this.#announce(this.dataset.labelA11yAdded ?? '');
        this.#flushNostoAttribution(detail?.items);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bt-cart-drawer] Event promise rejected:', error);
        }
      })
      .finally(() => {
        if (pending) this.#settleAdd(event, pending, ok);
        this.#syncDerivedState();
      });
  };

  /**
   * Shows the lines of an add the moment it is dispatched: a greyed row built from the
   * source's preview (or a neutral skeleton), or the existing row of that variant marked
   * busy, since Shopify merges a repeated variant into its line.
   */
  #beginAdd(event) {
    const preview = event[PREVIEW_LINE] ?? null;
    const lines = Array.isArray(event.lines) ? event.lines : [];
    const variantIds = [...new Set(lines.map((line) => variantIdOf(line?.merchandiseId)).filter(Boolean))];
    if (!variantIds.length) variantIds.push('');

    const entries = [];

    for (const variantId of variantIds) {
      const shared = variantId ? this.#pendingPlaceholderFor(variantId) : null;
      if (shared) {
        entries.push({ kind: 'placeholder', row: shared });
        continue;
      }

      const existing = variantId ? this.#lineForVariant(variantId) : null;
      if (existing) {
        const key = existing.getAttribute('data-line-key') ?? '';
        this.#setLineState(key, 'busy');
        entries.push({ kind: 'busy', key });
        continue;
      }

      const row = this.#createPlaceholder(
        variantId,
        preview && variantIdOf(preview.variantId) === variantId ? preview : null
      );
      const list = row ? this.#ensureScaffold() : null;
      if (!row || !list) continue;

      list.prepend(row);
      entries.push({ kind: 'placeholder', row });
    }

    const scroller = this.refs.body.querySelector('.bt-cart-drawer__scroll');
    if (scroller instanceof HTMLElement) scroller.scrollTop = 0;

    const pending = { marker: cartPerformance.createStartingMarker(ADD_MARKER), entries };
    this.#pendingAdds.set(event, pending);
    this.#setState('loading');
    this.#announce(this.dataset.labelA11yLoading ?? '');

    return pending;
  }

  #settleAdd(event, pending, ok) {
    this.#pendingAdds.delete(event);

    for (const entry of pending.entries) {
      if (entry.kind === 'placeholder') {
        if (entry.row.hasAttribute('data-line-placeholder') && !this.#rowPending(entry.row)) entry.row.remove();
      } else if (!ok && !this.#lineRequests.has(entry.key)) {
        this.#setLineState(entry.key, 'idle');
      }
    }

    if (!ok) this.#dropEmptyScaffold();

    cartPerformance.measureFromMarker(pending.marker);
  }

  #createPlaceholder(variantId, preview) {
    const template = this.refs.placeholderTemplate;
    const row = template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null;
    if (!(row instanceof HTMLElement)) return null;

    if (variantId) row.dataset.variantId = variantId;

    // Image, title and size only: the price comes with the real line, as the card and PDP
    // price markup is not clean enough to copy as text.
    const hasPreview = Boolean(preview && (preview.title || preview.imageSrc));
    if (!hasPreview) {
      row.toggleAttribute('data-skeleton', true);
      return row;
    }

    const image = row.querySelector('img');
    if (image instanceof HTMLImageElement && preview.imageSrc) {
      image.src = preview.imageSrc;
      image.hidden = false;
    }

    const title = row.querySelector('[data-placeholder-title]');
    if (title) title.textContent = preview.title ?? '';

    const size = row.querySelector('[data-placeholder-size]');
    if (size) {
      if (preview.size) size.textContent = preview.size;
      else size.remove();
    }

    return row;
  }

  /** The list placeholder rows go into, built over the hidden empty state when the bag is empty. */
  #ensureScaffold() {
    const { body } = this.refs;

    const list = body.querySelector('.bt-cart-drawer__lines');
    if (list instanceof HTMLElement) return list;

    const template = this.refs.scaffoldTemplate;
    const scaffold = template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null;
    if (!(scaffold instanceof HTMLElement)) return null;

    const empty = body.querySelector('#BtCartDrawer-empty');
    if (empty instanceof HTMLElement) {
      empty.hidden = true;
      empty.before(scaffold);
    } else {
      body.append(scaffold);
    }

    const created = scaffold.querySelector('.bt-cart-drawer__lines');
    return created instanceof HTMLElement ? created : null;
  }

  #dropEmptyScaffold() {
    const scaffold = this.refs.body.querySelector('[data-scaffold]');
    if (!(scaffold instanceof HTMLElement) || scaffold.querySelector('.bt-cart-drawer__line')) return;

    scaffold.remove();

    const empty = this.refs.body.querySelector('#BtCartDrawer-empty');
    if (empty instanceof HTMLElement) empty.hidden = false;
  }

  #lineForVariant(variantId) {
    const row = this.refs.body.querySelector(
      `.bt-cart-drawer__line[data-line-key][data-variant-id="${CSS.escape(variantId)}"]:not([data-line-gift])`
    );
    return row instanceof HTMLElement ? row : null;
  }

  #pendingPlaceholderFor(variantId) {
    for (const pending of this.#pendingAdds.values()) {
      for (const entry of pending.entries) {
        if (entry.kind === 'placeholder' && entry.row.isConnected && entry.row.dataset.variantId === variantId) {
          return entry.row;
        }
      }
    }
    return null;
  }

  #rowPending(row) {
    for (const pending of this.#pendingAdds.values()) {
      if (pending.entries.some((entry) => entry.kind === 'placeholder' && entry.row === row)) return true;
    }
    return false;
  }

  #detachPendingPlaceholders(except) {
    const detached = [];

    for (const pending of this.#pendingAdds.values()) {
      if (pending === except) continue;
      for (const entry of pending.entries) {
        if (entry.kind !== 'placeholder' || !entry.row.isConnected || detached.includes(entry.row)) continue;
        entry.row.remove();
        detached.push(entry.row);
      }
    }

    return detached;
  }

  #restorePendingPlaceholders(rows) {
    for (const row of rows) {
      if (!this.#rowPending(row)) continue;

      const variantId = row.dataset.variantId ?? '';
      const existing = variantId ? this.#lineForVariant(variantId) : null;

      if (existing) {
        const key = existing.getAttribute('data-line-key') ?? '';
        this.#setLineState(key, 'busy');
        this.#replacePlaceholderEntries(row, { kind: 'busy', key });
        continue;
      }

      this.#ensureScaffold()?.prepend(row);
    }
  }

  #replacePlaceholderEntries(row, entry) {
    for (const pending of this.#pendingAdds.values()) {
      pending.entries = pending.entries.map((candidate) =>
        candidate.kind === 'placeholder' && candidate.row === row ? { ...entry } : candidate
      );
    }
  }

  async #openQuickAdd(trigger) {
    const item = trigger.closest('[data-cross-sell-item]');
    if (!(item instanceof HTMLElement)) return;

    const productUrl = item.dataset.productUrl ?? '';
    if (!productUrl) return;

    this.#pendingNostoAdd =
      item.dataset.nostoProductId && item.dataset.nostoResultId
        ? { productId: item.dataset.nostoProductId, resultId: item.dataset.nostoResultId }
        : null;

    if (this.dataset.quickAddMode === 'simple' && (await this.#openSimpleQuickAdd(item, trigger))) return;

    const drawer = (document.getElementById('quick-add-drawer'));
    if (!drawer) return;

    if (typeof drawer.openForProduct !== 'function') {
      await customElements.whenDefined('quick-add-drawer');
    }
    if (typeof drawer.openForProduct === 'function') await drawer.openForProduct(productUrl);
  }

  async #openSimpleQuickAdd(item, trigger) {
    const handle = item.dataset.productHandle ?? '';
    if (!handle) return false;

    const sheet = (document.getElementById('bt-cart-quick-add'));
    if (!sheet) return false;

    if (typeof sheet.openForProduct !== 'function') await customElements.whenDefined('bt-cart-quick-add');
    if (typeof sheet.openForProduct !== 'function') return false;

    const image = item.querySelector('[data-cross-sell-media] img');

    return sheet.openForProduct({
      handle,
      url: item.dataset.productUrl ?? '',
      trigger,
      title: item.querySelector('[data-cross-sell-title]')?.textContent?.trim() ?? '',
      imageSrc: image instanceof HTMLImageElement ? image.currentSrc || image.src : '',
      imageSrcset: image instanceof HTMLImageElement ? image.srcset : '',
      priceHtml: item.querySelector('[data-cross-sell-price]')?.innerHTML ?? '',

      colour: item.dataset.colour ?? '',
    });
  }

  #flushNostoAttribution(items) {
    const pending = this.#pendingNostoAdd;
    this.#pendingNostoAdd = null;
    if (!pending) return;

    const added = Array.isArray(items) ? items : [];
    const matches = added.some((item) => String(item?.product_id ?? '') === String(pending.productId));
    if (added.length && !matches) return;

    this.#crossSell.attributeAdd(pending.productId, pending.resultId);
  }

  #syncNostoCart() {
    const target = document.querySelector('.nosto_cart');
    const snapshot = this.refs.body.querySelector('script[data-nosto-cart]');
    if (!(target instanceof HTMLElement) || !(snapshot instanceof HTMLScriptElement)) return;

    let lines = [];
    try {
      lines = JSON.parse(snapshot.textContent || '[]');
    } catch {
      return;
    }

    const fragment = document.createDocumentFragment();

    for (const line of lines) {
      const row = document.createElement('div');
      row.className = 'line_item';

      const fields = [
        ['product_id', String(line.product_id ?? '')],
        ['sku_id', String(line.sku_id ?? '')],
        ['quantity', String(line.quantity ?? '')],
        ['name', String(line.name ?? '')],
        ['unit_price', String(line.unit_price ?? '')],
        ['price_value', String(line.unit_price ?? '')],
        ['price_currency_code', String(line.price_currency_code ?? '')],
      ];

      for (const [className, value] of fields) {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = value;
        row.append(span);
      }

      fragment.append(row);
    }

    target.replaceChildren(fragment);
  }

  static #CARD_SELECTORS = {
    'shop-looks': '.bt-cart-drawer__look',
    'cross-sell': '.bt-cart-drawer__cross-sell-item',
  };

  #bindCarousels() {
    for (const carousel of this.#carousels) carousel.disconnect();
    this.#carousels = [];

    for (const root of this.refs.body.querySelectorAll('[data-bt-cart-carousel]')) {
      if (!(root instanceof HTMLElement) || root.hidden) continue;

      const track = (
        root.querySelector('[data-bt-cart-carousel-track]')
      );
      const kind = root.dataset.btCartCarousel ?? '';

      this.#carousels.push(
        bindOverflowCarousel({
          root,
          track,
          cardSelector: BtCartDrawer.#CARD_SELECTORS[kind] ?? '.bt-cart-drawer__carousel-card',
          progressVar: '--bt-cart-drawer-carousel-progress',
          features: { progress: true, nav: false },
        })
      );

      this.#syncOverflowFlag(root, track);
      if (track) {
        const observer = new ResizeObserver(() => this.#syncOverflowFlag(root, track));
        observer.observe(track);
        this.#carousels.push({ disconnect: () => observer.disconnect() });
      }
    }
  }

  #syncOverflowFlag(root, track) {
    const overflowing = Boolean(track) && track.scrollWidth - track.clientWidth > 1;
    root.toggleAttribute('data-overflowing', overflowing);
  }

  async #fillRecentlyViewed() {
    const section = this.refs.body.querySelector('[data-bt-cart-carousel="recently-viewed"]');
    if (!(section instanceof HTMLElement)) return;

    const placementId = section.dataset.nostoPlacement?.trim();
    const track = section.querySelector('[data-bt-cart-carousel-track]');
    if (!placementId || !(track instanceof HTMLElement)) return;

    this.#recentlyViewedController?.abort();
    this.#recentlyViewedController = new AbortController();
    const { signal } = this.#recentlyViewedController;

    try {
      const api = await getNostoApi(signal, { requireSearch: false });
      if (!api || signal.aborted) return;

      const response = await  (api)
        .createRecommendationRequest({ includeTagging: true })
        .setResponseMode('JSON_ORIGINAL')
        .setElements([placementId])
        .load();
      if (signal.aborted) return;

      const campaign = campaignFromResponse(response, placementId);
      const limit = Number(section.dataset.limit || 8);
      const handles = [
        ...new Set(campaignProducts(campaign).map(productHandle).filter(Boolean)),
      ].slice(0, limit);
      if (!handles.length) return;

      const cards = await fetchCardBatch(handles, signal);
      if (signal.aborted || !cards.size) return;

      const nodes = handles
        .map((handle) => cards.get(handle))
        .filter(Boolean)
        .map((card) => {
          const clone = ((card).cloneNode(true));
          suffixCardDomIds(clone);

          const wrap = document.createElement('div');
          wrap.className = 'bt-cart-drawer__carousel-card';
          wrap.append(clone);
          return wrap;
        });
      if (!nodes.length) return;

      track.replaceChildren(...nodes);
      section.hidden = false;
      this.#bindCarousels();

      if (
        trackingAllowed() &&
        campaign &&
        typeof ((api).attributeProductClicksInCampaign) === 'function'
      ) {
        (api).attributeProductClicksInCampaign(track, campaign);
      }
    } catch (error) {
      if ((error)?.name !== 'AbortError') {
        console.warn('[bt-cart-drawer] Recently viewed failed', error);
      }
    }
  }

  #suffixCardIds() {
    for (const card of this.refs.body.querySelectorAll(
      '.bt-cart-drawer__carousel-card > product-card'
    )) {
      suffixCardDomIds(card);
    }
  }

  #openShopTheLook(lookId, trigger) {
    const drawer = (
      document.getElementById('shop-the-look-drawer-cart-drawer')
    );

    if (drawer && typeof drawer.openLook === 'function') {
      drawer.openLook(lookId, trigger);
      return;
    }

    customElements.whenDefined('shop-the-look-drawer').then(() => drawer?.openLook?.(lookId, trigger));
  }

  async #toggleWishlist(button) {
    const row = button.closest('[data-line-key]');
    const product = toProduct({
      productId: row?.getAttribute('data-product-id') ?? '',
      variantId: row?.getAttribute('data-variant-id') ?? '',
      url: row?.querySelector('.bt-cart-drawer__line-title')?.getAttribute('href') ?? '',
    });
    if (!product || button.dataset.busy === 'true') return;

    const wanted = button.getAttribute('aria-pressed') !== 'true';
    button.dataset.busy = 'true';
    this.#setWishlistState(button, wanted);

    const result = await toggleWishlistEntry(product, wanted);
    delete button.dataset.busy;

    if (result === null) {
      this.#setWishlistState(button, !wanted);
      return;
    }

    this.#wishlist?.[wanted ? 'add' : 'delete'](productKey(product));
  }

  #setWishlistState(button, inList) {
    button.setAttribute('aria-pressed', String(inList));
    const label = inList ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }

  async #syncWishlist() {
    const keys = await wishlistKeys();
    if (!keys) return;

    this.#wishlist = keys;
    this.#paintWishlist();
  }

  #paintWishlist() {
    const keys = this.#wishlist;
    if (!keys) return;

    for (const button of this.refs.body.querySelectorAll('[data-line-wishlist]')) {
      if (!(button instanceof HTMLElement) || button.dataset.busy === 'true') continue;
      const row = button.closest('[data-line-key]');
      const product = toProduct({
        productId: row?.getAttribute('data-product-id') ?? '',
        variantId: row?.getAttribute('data-variant-id') ?? '',
      });
      if (!product) continue;
      this.#setWishlistState(button, keys.has(productKey(product)));
    }
  }

  #lineKeyOf(node) {
    return node.closest('[data-line-key]')?.getAttribute('data-line-key') ?? '';
  }

  #lineRow(lineKey) {
    const row = lineKey
      ? this.refs.body.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`)
      : null;
    return row instanceof HTMLElement ? row : null;
  }

  #lineInput(lineKey) {
    const input = this.#lineRow(lineKey)?.querySelector('[data-qty-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #bounds(input) {
    return {
      min: Number(input.min) || 1,
      max: input.max === '' ? Infinity : Number(input.max),
      step: Number(input.step) || 1,
    };
  }

  #updateStepperBounds(lineKey, quantity) {
    const row = this.#lineRow(lineKey);
    const input = this.#lineInput(lineKey);
    if (!row || !input) return;

    const { min, max } = this.#bounds(input);
    const minus = row.querySelector('[data-qty-minus]');
    const plus = row.querySelector('[data-qty-plus]');

    if (minus instanceof HTMLButtonElement) minus.disabled = quantity <= min;
    if (plus instanceof HTMLButtonElement) plus.disabled = quantity >= max;
  }
}

async function fetchCardBatch(handles, signal) {
  const cards = new Map();

  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}search?q=${encodeURIComponent(handles.join(','))}&type=product&view=bt-product-cards`;

  const response = await fetch(url, { signal });
  if (!response.ok) return cards;

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

  for (const wrap of doc.querySelectorAll('[data-product-handle]')) {
    if (!(wrap instanceof HTMLElement)) continue;
    const handle = wrap.dataset.productHandle;
    const card = wrap.querySelector('product-card');
    if (handle && card) cards.set(handle, card);
  }

  return cards;
}

function suffixCardDomIds(root) {
  const remap = new Map();

  const apply = (el) => {
    if (!el.id || el.id.endsWith(CARD_ID_SUFFIX)) return;
    const next = `${el.id}${CARD_ID_SUFFIX}`;
    remap.set(el.id, next);
    el.id = next;
  };

  apply(root);
  for (const el of root.querySelectorAll('[id]')) apply(el);
  if (!remap.size) return;

  const rewrite = (el, attr) => {
    const value = el.getAttribute(attr);
    if (!value) return;
    el.setAttribute(
      attr,
      value
        .split(/\s+/)
        .map((id) => remap.get(id) || id)
        .join(' ')
    );
  };

  const scoped = [
    root,
    ...root.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [for]'),
  ];

  for (const el of scoped) {
    rewrite(el, 'aria-labelledby');
    rewrite(el, 'aria-describedby');
    rewrite(el, 'aria-controls');
    rewrite(el, 'for');
  }
}

export class BtCartDrawerOpenEvent extends CustomEvent {
  static eventName = 'bt-cart-drawer:open';

  constructor(itemCount) {
    super(BtCartDrawerOpenEvent.eventName, { bubbles: true, detail: { itemCount } });
  }
}

export class BtCartDrawerCloseEvent extends CustomEvent {
  static eventName = 'bt-cart-drawer:close';

  constructor() {
    super(BtCartDrawerCloseEvent.eventName, { bubbles: true });
  }
}

export class BtCartDrawerUpdatedEvent extends CustomEvent {
  static eventName = 'bt-cart-drawer:updated';

  constructor(detail) {
    super(BtCartDrawerUpdatedEvent.eventName, { bubbles: true, detail });
  }
}

if (!customElements.get('bt-cart-drawer')) {
  customElements.define('bt-cart-drawer', BtCartDrawer);
}

